import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import useSWR from 'swr';
import { Skeleton } from '@mui/material';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Tooltip,
    Filler,
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';
import { fetcher, dashboardApi } from '../../../lib/api';
import formatWithThousandSeparator from 'utils/thousand_separator';
import { useThemeMode } from '../../../theme/ThemeProvider';
import styles from './AnalyticsLive.module.scss';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Tooltip, Filler);

const REFRESH_INTERVAL = 60000;

const sparklineOptions = {
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    plugins: {
        legend: { display: false },
        tooltip: { enabled: false },
        datalabels: { display: false },
    },
    scales: {
        x: { display: false },
        y: { display: false, beginAtZero: true },
    },
};

const PERIODS = ['24h', '30d', '90d', 'all'];

const AnalyticsLive = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const { isDark } = useThemeMode();
    const [period, setPeriod] = useState('24h');
    const { data, error, isLoading } = useSWR(dashboardApi.analyticsLive, fetcher, {
        refreshInterval: REFRESH_INTERVAL,
        revalidateOnFocus: true,
        revalidateOnReconnect: true,
        shouldRetryOnError: false,
    });

    const barOptions = useMemo(
        () => ({
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: { display: false },
                tooltip: { enabled: true },
                datalabels: { display: false },
            },
            scales: {
                x: {
                    reverse: true,
                    grid: { display: false },
                    ticks: {
                        maxRotation: 0,
                        autoSkip: true,
                        maxTicksLimit: 12,
                        font: { size: 10 },
                        color: isDark ? '#a0a8bc' : '#666b77',
                    },
                },
                y: {
                    beginAtZero: true,
                    ticks: {
                        precision: 0,
                        color: isDark ? '#a0a8bc' : '#666b77',
                    },
                    grid: {
                        color: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0, 8, 28, 0.05)',
                    },
                },
            },
        }),
        [isDark]
    );

    const results = data?.results;
    const activeNow = results?.active_users_now ?? 0;
    const totalDay = results?.total_users_24h ?? 0;
    const active30d = results?.active_users_30d ?? 0;
    const series30d = useMemo(() => results?.active_users_30d_series ?? [], [results]);
    const series90d = useMemo(() => results?.active_users_90d_series ?? [], [results]);
    const seriesAll = useMemo(() => results?.active_users_all_series ?? [], [results]);
    const offerOpensNow = results?.offer_opens_now_count ?? 0;
    const offerOpensNowUsers = results?.offer_opens_now_users ?? 0;
    const offerOpens24h = results?.offer_opens_24h_count ?? 0;
    const topOffersNow = useMemo(() => results?.top_offers_now ?? [], [results]);
    const topScreens24h = useMemo(() => results?.top_screens_24h ?? [], [results]);
    const perMinute = useMemo(() => results?.per_minute ?? [], [results]);
    const last24h = useMemo(() => results?.last_24h ?? [], [results]);

    const sparklineData = useMemo(
        () => ({
            labels: perMinute.map((point) => point.minute),
            datasets: [
                {
                    data: perMinute.map((point) => point.active_users),
                    borderColor: '#00a86b',
                    backgroundColor: 'rgba(27, 242, 164, 0.18)',
                    fill: true,
                    tension: 0.35,
                    borderWidth: 2,
                    pointRadius: 0,
                },
            ],
        }),
        [perMinute]
    );

    // Charts store buckets newest-first (index 0 = most recent) so the latest bar keeps a
    // stable index across periods; the x-axis reverse:true restores chronological display.
    const barData = useMemo(() => {
        const buckets = [...last24h].reverse();
        return {
            labels: buckets.map((bucket) => bucket.hour.slice(11, 16)),
            datasets: [
                {
                    label: t('dash.live_24h'),
                    data: buckets.map((bucket) => bucket.active_users),
                    backgroundColor: '#16b4f3',
                    borderRadius: 4,
                    maxBarThickness: 18,
                },
            ],
        };
    }, [last24h, t]);

    const buildDailyBarData = (series, color) => {
        const points = [...series].reverse();
        return {
            labels: points.map((point) => point.date.slice(5)),
            datasets: [
                {
                    data: points.map((point) => point.active_users),
                    backgroundColor: color,
                    borderRadius: 3,
                    maxBarThickness: 18,
                },
            ],
        };
    };

    const barData30d = useMemo(() => buildDailyBarData(series30d, '#db18ec'), [series30d]);
    const barData90d = useMemo(() => buildDailyBarData(series90d, '#00a86b'), [series90d]);

    const monthlyData = useMemo(() => {
        const points = [...seriesAll].reverse();
        return {
            labels: points.map((point) => point.month),
            datasets: [
                {
                    label: t('dash.live_all'),
                    data: points.map((point) => point.active_users),
                    backgroundColor: '#f2cb04',
                    borderRadius: 4,
                    maxBarThickness: 24,
                },
            ],
        };
    }, [seriesAll, t]);

    const activeChart = useMemo(() => {
        switch (period) {
            case '30d':
                return <Bar data={barData30d} options={barOptions} />;
            case '90d':
                return <Bar data={barData90d} options={barOptions} />;
            case 'all':
                return <Bar data={monthlyData} options={barOptions} />;
            case '24h':
            default:
                return <Bar data={barData} options={barOptions} />;
        }
    }, [period, barData30d, barData90d, monthlyData, barData]);

    return (
        <div className={styles.card}>
            <div className={styles.header}>
                <h3 className={styles.title}>{t('dash.live_title')}</h3>
                <span className={styles.liveBadge}>
                    <span className={styles.liveDot} />
                    {t('dash.live_badge')}
                </span>
            </div>

            {error ? (
                <div className={styles.errorState}>{t('dash.live_error')}</div>
            ) : (
                <>
                    <div className={styles.statsRow}>
                        <div className={styles.statBlock}>
                            <span className={styles.statLabel}>{t('dash.live_active_now')}</span>
                            <span className={styles.statValue}>
                                {isLoading ? (
                                    <Skeleton width={90} height={48} />
                                ) : (
                                    formatWithThousandSeparator(activeNow)
                                )}
                            </span>
                            <span className={styles.statSub}>{t('dash.live_last_30_min')}</span>
                            <div className={styles.sparkline}>
                                {!isLoading && perMinute.length > 0 && (
                                    <Line data={sparklineData} options={sparklineOptions} />
                                )}
                            </div>
                        </div>

                        <div className={styles.statBlock}>
                            <span className={styles.statLabel}>{t('dash.live_total_24h')}</span>
                            <span className={styles.statValue}>
                                {isLoading ? (
                                    <Skeleton width={90} height={48} />
                                ) : (
                                    formatWithThousandSeparator(totalDay)
                                )}
                            </span>
                            <span className={styles.statSub}>{t('dash.live_24h')}</span>
                        </div>

                        <div className={styles.statBlock}>
                            <span className={styles.statLabel}>{t('dash.live_30d')}</span>
                            <span className={styles.statValue}>
                                {isLoading ? (
                                    <Skeleton width={90} height={48} />
                                ) : (
                                    formatWithThousandSeparator(active30d)
                                )}
                            </span>
                        </div>
                    </div>

                    <div className={styles.periodSelector}>
                        {PERIODS.map((key) => (
                            <button
                                key={key}
                                type="button"
                                className={`${styles.periodButton} ${
                                    period === key ? styles.periodButtonActive : ''
                                }`}
                                onClick={() => setPeriod(key)}
                            >
                                {t(`dash.live_period_${key}`)}
                            </button>
                        ))}
                    </div>

                    <div className={styles.chartWrap}>
                        {isLoading ? (
                            <Skeleton variant="rectangular" width="100%" height={220} />
                        ) : (
                            activeChart
                        )}
                    </div>

                    <div className={styles.offersSection}>
                        <div className={styles.statsRow}>
                            <div className={styles.statBlock}>
                                <span className={styles.statLabel}>
                                    {t('dash.live_offer_opens_now')}
                                </span>
                                <span className={styles.statValue}>
                                    {isLoading ? (
                                        <Skeleton width={90} height={48} />
                                    ) : (
                                        formatWithThousandSeparator(offerOpensNow)
                                    )}
                                </span>
                                <span className={styles.statSub}>
                                    {t('dash.live_offer_opens_now_users', {
                                        count: offerOpensNowUsers,
                                    })}
                                </span>
                            </div>

                            <div className={styles.statBlock}>
                                <span className={styles.statLabel}>
                                    {t('dash.live_offer_opens_24h')}
                                </span>
                                <span className={styles.statValue}>
                                    {isLoading ? (
                                        <Skeleton width={90} height={48} />
                                    ) : (
                                        formatWithThousandSeparator(offerOpens24h)
                                    )}
                                </span>
                            </div>
                        </div>

                        <div className={styles.topOffers}>
                            <span className={styles.topOffersTitle}>
                                {t('dash.live_top_offers_now')}
                            </span>
                            {isLoading ? (
                                <div className={styles.offerCards}>
                                    {[0, 1, 2, 3].map((key) => (
                                        <Skeleton
                                            key={key}
                                            variant="rectangular"
                                            height={168}
                                            className={styles.offerCardSkeleton}
                                        />
                                    ))}
                                </div>
                            ) : topOffersNow.length === 0 ? (
                                <div className={styles.topOffersEmpty}>{t('dash.no_data')}</div>
                            ) : (
                                <div className={styles.offerCards}>
                                    {topOffersNow.map((offer, index) => {
                                        const title =
                                            i18n.language === 'en' && offer.title_en
                                                ? offer.title_en
                                                : offer.title || `#${offer.offer_id}`;
                                        const inactive = offer.is_active === false;
                                        return (
                                            <button
                                                key={offer.offer_id}
                                                type="button"
                                                className={`${styles.offerCard}${
                                                    inactive ? ` ${styles.offerCardInactive}` : ''
                                                }`}
                                                onClick={() =>
                                                    navigate(
                                                        `/offers/${offer.offer_id}/statistics`
                                                    )
                                                }
                                            >
                                                <span className={styles.offerCardRank}>
                                                    {index + 1}
                                                </span>
                                                <div className={styles.offerCardImageWrap}>
                                                    {offer.image ? (
                                                        <img
                                                            src={offer.image}
                                                            alt=""
                                                            className={styles.offerCardImage}
                                                        />
                                                    ) : (
                                                        <div
                                                            className={styles.offerCardImagePlaceholder}
                                                        />
                                                    )}
                                                    {offer.discount ? (
                                                        <span className={styles.offerCardDiscount}>
                                                            {offer.discount}
                                                        </span>
                                                    ) : null}
                                                </div>
                                                <div className={styles.offerCardBody}>
                                                    <span className={styles.offerCardTitle}>
                                                        {title}
                                                    </span>
                                                    {offer.company_name ? (
                                                        <span className={styles.offerCardCompany}>
                                                            {offer.company_name}
                                                        </span>
                                                    ) : null}
                                                    <span className={styles.offerCardOpens}>
                                                        {formatWithThousandSeparator(offer.opens)}
                                                    </span>
                                                </div>
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        <div className={styles.topOffers}>
                            <span className={styles.topOffersTitle}>
                                {t('dash.live_top_screens')}
                            </span>
                            {isLoading ? (
                                <Skeleton variant="rectangular" width="100%" height={120} />
                            ) : topScreens24h.length === 0 ? (
                                <div className={styles.topOffersEmpty}>{t('dash.no_data')}</div>
                            ) : (
                                <ol className={styles.topOffersList}>
                                    {topScreens24h.map((row) => (
                                        <li key={row.screen} className={styles.topOffersItem}>
                                            <span className={styles.topOffersName}>
                                                {row.screen}
                                            </span>
                                            <span className={styles.topOffersCount}>
                                                {formatWithThousandSeparator(row.opens)}
                                            </span>
                                        </li>
                                    ))}
                                </ol>
                            )}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default AnalyticsLive;
